/**
 * Axios client with authentication interceptors
 * Requirements: 12.5 - 401 Unauthorized handling
 */
import axios from 'axios';
import type {
  AxiosInstance,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  isEmbedded,
  getInjectedToken,
  requestTokenRefresh,
  setInjectedToken,
  notifyUnauthorized,
} from './embeddedAuth';

export interface InterceptorOptions {
  onUnauthorized: () => void;
}

/**
 * Create an Axios client instance
 */
export const createApiClient = (config?: CreateAxiosDefaults): AxiosInstance => {
  return axios.create({
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    ...config,
  });
};

/**
 * Setup authentication interceptors for the Axios client
 */
export const setupInterceptors = (
  client: AxiosInstance,
  options: InterceptorOptions
): AxiosInstance => {
  // Request interceptor - add Authorization header
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      try {
        if (isEmbedded()) {
          // WebView埋め込み時: ネイティブから注入された idToken を使用（Amplify非依存）
          const injected = getInjectedToken();
          if (injected) {
            config.headers.Authorization = `Bearer ${injected}`;
          }
        } else {
          const session = await fetchAuthSession();
          // Use idToken for API Gateway Cognito Authorizer
          const idToken = session.tokens?.idToken;
          if (idToken) {
            config.headers.Authorization = `Bearer ${idToken.toString()}`;
          }
        }
      } catch {
        // No token available, continue without auth header
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors and CORS failures (e.g. expired token)
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check if it's a 401 error OR a network/CORS error (no response object).
      // When API Gateway rejects a request due to expired Cognito token,
      // the 401 response may lack CORS headers, causing the browser to
      // treat it as a CORS error with error.response === undefined.
      const is401 = error.response?.status === 401;
      const isCorsOrNetworkError = !error.response && error.code !== 'ECONNABORTED';

      if ((is401 || isCorsOrNetworkError) && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          if (isEmbedded()) {
            // WebView埋め込み時: ネイティブへリフレッシュ要求 → 新idToken を待って再試行
            const fresh = await requestTokenRefresh();
            if (!fresh) {
              notifyUnauthorized();
              return Promise.reject(error);
            }
            setInjectedToken(fresh);
          } else {
            // Attempt to refresh the token
            await fetchAuthSession({ forceRefresh: true });
          }

          // Retry the original request
          return client(originalRequest);
        } catch {
          // Token refresh failed
          if (isEmbedded()) {
            notifyUnauthorized();
          } else {
            options.onUnauthorized();
          }
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Create a configured API client with authentication
 */
export const createAuthenticatedClient = (
  baseURL: string,
  onUnauthorized: () => void
): AxiosInstance => {
  const client = createApiClient({ baseURL });
  return setupInterceptors(client, { onUnauthorized });
};
